import { useState } from "react";
import { Tabs, Card, Slider, Switch, InputNumber, Button, Table, Space, Typography, Row, Col, Popconfirm, Input, Select, message, Divider, Tooltip } from "antd";
import { PlusOutlined, DeleteOutlined, InfoCircleOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import {
  useGetAffinityConfigQuery,
  useUpdateAffinityConfigMutation,
  useGetPrioritizationTablesQuery,
  useCreatePrioritizationTableMutation,
  useUpdatePrioritizationTableMutation,
  useDeletePrioritizationTableMutation,
  useCreatePrioritizationEntryMutation,
  useUpdatePrioritizationEntryMutation,
  useDeletePrioritizationEntryMutation,
} from "../../../redux/api/scheduling";

const { Title, Text } = Typography;

const TipIcon = ({ tip }: { tip: string }) => (
  <Tooltip title={tip} overlayStyle={{ maxWidth: 360 }}>
    <QuestionCircleOutlined style={{ marginLeft: 6, color: "#8c8c8c", cursor: "help" }} />
  </Tooltip>
);

const OVERVIEW_TOOLTIP = [
  "Machine Affinity Preference Score",
  "━━━━━━━━━━━━━━━━━━━━━━━━━━━",
  "Preference (0-1)",
  "  ├─ Urgency \u00d7 weight%",
  "  │   ├─ Lead Time \u00d7 weight%",
  "  │   ├─ Calculated Slack \u00d7 weight%",
  "  │   └─ Timeliness \u00d7 weight%",
  "  ├─ Runtime Profile \u00d7 weight%",
  "  │   (Short / Medium / Long machine time)",
  "  └─ Run Difficulty \u00d7 weight%",
  "",
  "Score near 1.0 \u2192 prefers attended machines",
  "Score near 0.0 \u2192 tolerates unattended machines",
  "",
  "This is a soft preference, not a hard rule.",
  "Actual timing differences still dominate.",
].join("\n");

const LinkedSliders = ({
  values,
  labels,
  tooltips,
  onChange,
}: {
  values: number[];
  labels: string[];
  tooltips?: string[];
  onChange: (vals: number[]) => void;
}) => {
  const handleChange = (idx: number, newVal: number) => {
    if (newVal < 0 || newVal > 100) return;

    const newValues = [...values];
    newValues[idx] = newVal;
    const remaining = 100 - newVal;

    const otherEntries = values
      .map((v, i) => ({ i, v }))
      .filter(({ i }) => i !== idx);
    const otherSum = otherEntries.reduce((sum, { v }) => sum + v, 0);

    if (otherSum === 0 && remaining === 0) {
      otherEntries.forEach(({ i }) => (newValues[i] = 0));
    } else if (otherSum > 0) {
      const scale = remaining / otherSum;
      let assigned = 0;
      for (let j = 0; j < otherEntries.length; j++) {
        const { i, v } = otherEntries[j];
        if (j === otherEntries.length - 1) {
          newValues[i] = remaining - assigned;
        } else {
          newValues[i] = Math.round(v * scale);
          assigned += newValues[i];
        }
      }
    } else {
      let assigned = 0;
      for (let j = 0; j < otherEntries.length; j++) {
        const { i } = otherEntries[j];
        if (j === otherEntries.length - 1) {
          newValues[i] = remaining - assigned;
        } else {
          const share = Math.round(remaining / otherEntries.length);
          newValues[i] = share;
          assigned += share;
        }
      }
    }

    onChange(newValues);
  };

  return (
    <div>
      {labels.map((label, idx) => (
        <Row key={label} align="middle" gutter={16} style={{ marginBottom: 8 }}>
          <Col span={6}>
            <Text>{label}</Text>
            {tooltips?.[idx] && <TipIcon tip={tooltips[idx]} />}
          </Col>
          <Col span={14}>
            <Slider min={0} max={100} value={values[idx]} onChange={(v) => handleChange(idx, v)} />
          </Col>
          <Col span={4}><Text strong>{values[idx]}%</Text></Col>
        </Row>
      ))}
    </div>
  );
};

const AffinityTab = () => {
  const { data: config, isLoading } = useGetAffinityConfigQuery({});
  const [updateConfig] = useUpdateAffinityConfigMutation();
  const [dirty, setDirty] = useState<Record<string, any>>({});

  if (isLoading || !config) return <div>Loading...</div>;

  const current = { ...config, ...dirty };

  const handleField = (field: string, value: any) => {
    setDirty((prev) => ({ ...prev, [field]: value }));
  };

  const handleLinked = (keys: string[], values: number[]) => {
    const patch: Record<string, number> = {};
    keys.forEach((k, i) => (patch[k] = values[i]));
    setDirty((prev) => ({ ...prev, ...patch }));
  };

  const handleSave = async () => {
    if (Object.keys(dirty).length === 0) return;
    try {
      await updateConfig(dirty).unwrap();
      setDirty({});
      message.success("Affinity configuration saved");
    } catch {
      message.error("Failed to save");
    }
  };

  return (
    <div style={{ maxWidth: 800 }}>
      <Row align="middle" gutter={16} style={{ marginBottom: 24 }}>
        <Col>
          <Text strong>Enable Affinity Scoring</Text>
          <TipIcon tip="When enabled, the scheduler will use a soft preference to bias machine assignments based on job characteristics and machine staffing levels. When disabled, machines are chosen purely by earliest availability." />
        </Col>
        <Col>
          <Switch checked={current.enabled} onChange={(v) => handleField("enabled", v)} />
        </Col>
        <Col>
          <Tooltip
            title={<pre style={{ margin: 0, fontSize: 12, whiteSpace: "pre-wrap", fontFamily: "inherit" }}>{OVERVIEW_TOOLTIP}</pre>}
            overlayStyle={{ maxWidth: 420 }}
            placement="rightTop"
          >
            <Button size="small" icon={<InfoCircleOutlined />}>How It Works</Button>
          </Tooltip>
        </Col>
        <Col flex="auto" style={{ textAlign: "right" }}>
          <Button type="primary" onClick={handleSave} disabled={Object.keys(dirty).length === 0}>
            Save Affinity Configuration
          </Button>
        </Col>
      </Row>

      <Card
        title={<>Global Parameters<TipIcon tip="These two settings control the overall impact of affinity scoring. They work together: the final adjustment is Affinity Strength multiplied by Max Machine Time Adjust." /></>}
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Text>Affinity Strength</Text>
            <TipIcon tip="Controls how much influence affinity has overall (0% = no effect, 100% = full effect). This is blended with the Max Machine Time Adjust to determine the actual impact on machine selection." />
            <Slider min={0} max={100} value={current.affinity_strength} onChange={(v) => handleField("affinity_strength", v)} />
          </Col>
          <Col span={12}>
            <Text>Max Machine Time Adjust</Text>
            <TipIcon tip="Caps the maximum percentage that affinity can shrink or grow a machine's apparent time during comparison. At 15%, even a perfect match can only make a machine look 15% better or worse. This prevents the preference from overriding real timing differences." />
            <Slider min={1} max={50} value={current.max_shift_percent} onChange={(v) => handleField("max_shift_percent", v)} />
          </Col>
        </Row>
      </Card>

      <Card
        title={<>Preference Composition (must sum to 100%)<TipIcon tip="Controls the blend of three factors that determine a job's preference for attended vs. unattended machines. Adjust these sliders to emphasize urgency, runtime profile, or run difficulty. Moving one slider automatically adjusts the others to maintain 100%." /></>}
        size="small"
        style={{ marginBottom: 16 }}
      >
        <LinkedSliders
          values={[current.pref_weight_urgency, current.pref_weight_runtime, current.pref_weight_difficulty]}
          labels={["Urgency", "Runtime Profile", "Run Difficulty"]}
          tooltips={[
            "Urgency considers lead time priority, available slack, and timeliness status. Higher urgency jobs prefer attended (higher staffed) machines. Configure its sub-components below.",
            "Runtime Profile is based on total machine time. Short jobs prefer attended machines; long jobs tolerate unattended. Configure thresholds in the section below.",
            "Run Difficulty is set per job (1-10). Higher difficulty jobs prefer attended machines where staff can monitor them. Lower difficulty jobs can run unattended.",
          ]}
          onChange={(vals) => handleLinked(["pref_weight_urgency", "pref_weight_runtime", "pref_weight_difficulty"], vals)}
        />
      </Card>

      <Card
        title={<>Urgency Sub-Weights (must sum to 100%)<TipIcon tip="These three components make up the Urgency score from Preference Composition above. Adjust to control which urgency signals matter most." /></>}
        size="small"
        style={{ marginBottom: 16 }}
      >
        <LinkedSliders
          values={[current.weight_lead_time, current.weight_slack, current.weight_timeliness]}
          labels={["Lead Time", "Calculated Slack", "Timeliness"]}
          tooltips={[
            "Lead time priority code (EX1-STD). Uses the weighting values in the Lead Time Weighting section below.",
            "Dynamically calculated buffer between the deadline and remaining work. Less slack means more urgency.",
            "Current timeliness status of the job (Late, At Risk, On Track). Uses the weighting values in the Timeliness Weighting section below.",
          ]}
          onChange={(vals) => handleLinked(["weight_lead_time", "weight_slack", "weight_timeliness"], vals)}
        />
      </Card>

      <Card
        title={<>Lead Time Weighting (1-10)<TipIcon tip="Set the importance of each lead time code for the urgency calculation. Higher values (closer to 10) mean that lead time code contributes more urgency, pushing the job toward attended machines." /></>}
        size="small"
        style={{ marginBottom: 16 }}
      >
        {(["EX1", "EX2", "EX3", "EX4", "STD"] as const).map((code) => {
          const key = `score_${code.toLowerCase()}` as string;
          return (
            <Row key={code} align="middle" gutter={16} style={{ marginBottom: 4 }}>
              <Col span={4}><Text>{code}</Text></Col>
              <Col span={16}><Slider min={0} max={10} value={current[key]} onChange={(v) => handleField(key, v)} /></Col>
              <Col span={4}><Text strong>{current[key]}</Text></Col>
            </Row>
          );
        })}
      </Card>

      <Card
        title={<>Timeliness Weighting (1-10)<TipIcon tip="Set the importance of each timeliness status for the urgency calculation. Higher values mean that status contributes more urgency. For example, a high 'Late' value strongly pushes late jobs toward attended machines." /></>}
        size="small"
        style={{ marginBottom: 16 }}
      >
        {([["Late", "score_late"], ["At Risk", "score_at_risk"], ["On Track", "score_on_track"]] as const).map(([label, key]) => (
          <Row key={key} align="middle" gutter={16} style={{ marginBottom: 4 }}>
            <Col span={4}><Text>{label}</Text></Col>
            <Col span={16}><Slider min={0} max={10} value={current[key]} onChange={(v) => handleField(key, v)} /></Col>
            <Col span={4}><Text strong>{current[key]}</Text></Col>
          </Row>
        ))}
      </Card>

      <Card
        title={<>Runtime Profile Thresholds (in minutes of machine time)<TipIcon tip="These two values divide jobs into three runtime groups: Short (at or below the short threshold), Medium (between the two), and Long (at or above the long threshold). Short jobs strongly prefer attended machines, long jobs tolerate unattended, and medium jobs are blended proportionally." /></>}
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Text>Short Threshold</Text>
            <TipIcon tip="Jobs with machine time at or below this value are classified as Short and will strongly prefer attended machines." />
            <InputNumber style={{ width: "100%" }} min={0} value={current.short_threshold_minutes} onChange={(v) => handleField("short_threshold_minutes", v)} />
          </Col>
          <Col span={12}>
            <Text>Long Threshold</Text>
            <TipIcon tip="Jobs with machine time at or above this value are classified as Long and will tolerate unattended machines." />
            <InputNumber style={{ width: "100%" }} min={0} value={current.long_threshold_minutes} onChange={(v) => handleField("long_threshold_minutes", v)} />
          </Col>
        </Row>
      </Card>

    </div>
  );
};

const PriorityTablesTab = () => {
  const { data: tables = [], isLoading } = useGetPrioritizationTablesQuery({});
  const [createTable] = useCreatePrioritizationTableMutation();
  const [updateTable] = useUpdatePrioritizationTableMutation();
  const [deleteTable] = useDeletePrioritizationTableMutation();
  const [createEntry] = useCreatePrioritizationEntryMutation();
  const [updateEntry] = useUpdatePrioritizationEntryMutation();
  const [deleteEntry] = useDeletePrioritizationEntryMutation();
  const [expandedTable, setExpandedTable] = useState<number | null>(null);

  if (isLoading) return <div>Loading...</div>;

  const tableColumns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Pass", dataIndex: "schedule_pass", key: "schedule_pass" },
    { title: "Source", dataIndex: "variable_source", key: "variable_source" },
    { title: "Variable", dataIndex: "job_variable_label", key: "job_variable_label", render: (v: string, r: any) => v || r.job_task_variable_field || "-" },
    { title: "Order", dataIndex: "order", key: "order" },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) => (
        <Space>
          <Button size="small" onClick={() => setExpandedTable(expandedTable === record.id ? null : record.id)}>
            {expandedTable === record.id ? "Collapse" : "Entries"}
          </Button>
          <Popconfirm title="Delete this table?" onConfirm={() => deleteTable(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const entryColumns = [
    { title: "Value", dataIndex: "value", key: "value" },
    {
      title: "Priority Order",
      dataIndex: "priority_order",
      key: "priority_order",
      render: (val: number, record: any) => (
        <InputNumber
          size="small"
          min={0}
          value={val}
          onChange={(v) => v !== null && updateEntry({ id: record.id, priority_order: v })}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) => (
        <Popconfirm title="Delete?" onConfirm={() => deleteEntry(record.id)}>
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <Text type="secondary" style={{ marginBottom: 16, display: "block" }}>
        Priority tables control the ordering of jobs when multiple compete for the same resource.
        Tables are evaluated in ascending order. Within each table, entries define the sort priority for values of the chosen variable.
      </Text>
      <Table
        dataSource={Array.isArray(tables) ? tables : []}
        columns={tableColumns}
        rowKey="id"
        pagination={false}
        size="small"
        expandable={{
          expandedRowKeys: expandedTable ? [expandedTable] : [],
          onExpand: (expanded, record) => setExpandedTable(expanded ? record.id : null),
          expandedRowRender: (record: any) => {
            const entries = record.entries || [];
            return (
              <div style={{ padding: "8px 0" }}>
                <Table
                  dataSource={entries}
                  columns={entryColumns}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  style={{ marginTop: 8 }}
                  onClick={() => {
                    const value = prompt("Entry value:");
                    if (value) {
                      createEntry({ table: record.id, value, priority_order: entries.length });
                    }
                  }}
                >
                  Add Entry
                </Button>
              </div>
            );
          },
        }}
      />
    </div>
  );
};

const SchedulerConfig = () => {
  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Scheduler Configuration</Title>
      <Tabs
        defaultActiveKey="affinity"
        items={[
          { key: "affinity", label: "Machine Affinity", children: <AffinityTab /> },
          { key: "priority", label: "Priority Tables", children: <PriorityTablesTab /> },
        ]}
      />
    </div>
  );
};

export default SchedulerConfig;
